import React from "react";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import "./CustomDataGridTable.css";

type TableColumn = {
  field: string;
  headerName?: React.ReactNode;
  subHeaderName?: React.ReactNode;
  align?: "left" | "center" | "right";
};

type TableRowData = {
  id?: string | number;
  [key: string]: React.ReactNode;
};

export interface CustomDataGridTableProps {
  columns: TableColumn[];
  rows: TableRowData[];
  tableFooter?: boolean;
  toolbarFilter?: boolean;
  columnGroup1?: boolean;
  columnGroup2?: boolean;
  proHeaderFilters?: boolean;
  quickFilter?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
}

interface TableDisplayRow {
  id: string;
  [key: string]: React.ReactNode;
}


export const CustomDataGridTable = ({
  columns,
  rows,
  tableFooter = true,
  toolbarFilter = false,
  columnGroup1 = false,
  columnGroup2 = false,
  proHeaderFilters = false,
  quickFilter = false,
  sx,
  className,
}: CustomDataGridTableProps) => {
  const featureToggles =
    toolbarFilter || columnGroup1 || columnGroup2 || proHeaderFilters || quickFilter;
  const normalizedColumns = columns;
  const priceTypeColumn = normalizedColumns[0];
  const monthColumns = normalizedColumns.slice(1);
  const hasHeaderContent = (header: React.ReactNode) => {
    if (header === null || header === undefined) {
      return false;
    }

    if (typeof header === "string") {
      return header.trim().length > 0;
    }

    return true;
  };

  const monthHeaderGroups = monthColumns.reduce<
    Array<{ key: string; headerName: React.ReactNode; align?: "left" | "center" | "right"; colSpan: number }>
  >((groups, column) => {
    const headerHasContent = hasHeaderContent(column.headerName);

    if (headerHasContent || groups.length === 0) {
      groups.push({
        key: `header-${column.field}`,
        headerName: column.headerName ?? "",
        align: column.align,
        colSpan: 1,
      });
      return groups;
    }

    const lastIndex = groups.length - 1;
    return groups.map((group, index) =>
      index === lastIndex ? { ...group, colSpan: group.colSpan + 1 } : group,
    );
  }, []);
  const hasAnySubHeader = monthColumns.some(
    (column) => typeof column.subHeaderName === "string"
      ? column.subHeaderName.trim().length > 0
      : column.subHeaderName !== null && column.subHeaderName !== undefined,
  );
  const getDisplayPriceTypeLabel = (value: React.ReactNode) => {
    if (typeof value !== "string") return value;

    const trimmed = value.trim();
    const normalized: Record<string, string> = {
      "Posted Px": "Posted Price",
      "TRUCK ALT": "Truck ALT",
      "RAIL ALT": "Rail ALT",
    };

    return normalized[trimmed] ?? value;
  };
  const fieldOrder = normalizedColumns.map((column) => column.field);
  const tableContainerClassName = className
    ? `custom-card-data-grid-table ${className}`
    : "custom-card-data-grid-table";

  const displayRows: TableDisplayRow[] =
    rows.length > 0
      ? rows.map((row, index) => {
        const mappedRow: TableDisplayRow = {
          id: String(row.id ?? index),
        };
        fieldOrder.forEach((field) => {
          mappedRow[field] = row[field] ?? "";
        });
        return mappedRow;
      })
      : [];

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className={tableContainerClassName}
      sx={sx}
    >
      <Table aria-label="custom data grid table" className="custom-card-data-grid-table-inner">
        <TableHead>
          <TableRow id="month-header" className="custom-card-data-grid-header-row">
            <TableCell
              className="custom-card-data-grid-header-cell custom-card-data-grid-header-cell-price-type"
            >
              {priceTypeColumn?.headerName ?? "Price Type"}
            </TableCell>
            {monthHeaderGroups.map((group) => (
              <TableCell
                key={group.key}
                align={group.align ?? "center"}
                colSpan={group.colSpan}
                className="custom-card-data-grid-header-cell custom-card-data-grid-header-cell-month"
              >
                {group.headerName ?? ""}
              </TableCell>
            ))}
          </TableRow>
          {hasAnySubHeader && <TableRow id="month-split-subheader" className="custom-card-data-grid-header-row">
            <TableCell className="custom-card-data-grid-subheader-spacer-cell" />
            {monthColumns.map((column) => (
              <TableCell
                key={`subheader-${column.field}`}
                align={column.align ?? "center"}
                className="custom-card-data-grid-subheader-cell"
              >
                {column.subHeaderName ?? ""}
              </TableCell>
            ))}
          </TableRow>}
        </TableHead>
        <TableBody>
          {displayRows.map((row) => (
            <TableRow key={row.id} className="custom-card-data-grid-body-row">
              <TableCell className="custom-card-data-grid-body-cell custom-card-data-grid-body-cell-price-type">
                <span id={`price-type-label-${row.id}`} className="custom-card-data-grid-price-type-label">
                  {getDisplayPriceTypeLabel(row[priceTypeColumn?.field ?? "priceType"] ?? "")}
                </span>
              </TableCell>
              {monthColumns.map((column) => (
                <TableCell
                  key={`cell-${row.id}-${column.field}`}
                  align={column.align ?? "center"}
                  className="custom-card-data-grid-body-cell"
                >
                  {row[column.field] ?? ""}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {tableFooter && (
          <TableFooter>
            <TableRow className="custom-card-data-grid-footer-row">
              <TableCell
                colSpan={normalizedColumns.length}
                className="custom-card-data-grid-footer-cell"
              >
                <Button
                  size="small"
                  variant="text"
                  className={`custom-card-data-grid-add-row-btn${featureToggles ? " custom-card-data-grid-add-row-btn--with-feature" : ""}`}
                >
                  + Add Row
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};